import { Injectable, inject, signal, computed } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, catchError, finalize, firstValueFrom, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User, UserRole } from '../models/user.model';
import { ApiResponse } from '../models/api-response.model';
import { LEGACY_ROLE_TABS } from '../access/tabs';

/** Por que a sessão terminou sem a pessoa pedir: muda a mensagem que o login mostra. */
export type MotivoSaida = 'expirada' | 'indisponivel';

interface AuthState {
  user: User | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly document = inject(DOCUMENT);
  private readonly baseUrl = environment.apiUrl;

  private readonly authState = signal<AuthState>({
    user: null,
  });

  readonly user = computed(() => this.authState().user);
  readonly isAuthenticated = computed(() => !!this.authState().user);
  readonly userRole = computed(() => this.authState().user?.role || null);

  /**
   * Restore session from httpOnly cookies via GET /user/me.
   * Used at bootstrap so guards see the user before the first navigation.
   */
  restoreSession(): Promise<void> {
    return firstValueFrom(
      this.http.get<ApiResponse<User>>(`${this.baseUrl}/user/me`).pipe(
        tap((response) => this.authState.set({ user: response.result })),
        map(() => undefined),
        catchError(() => {
          this.clearAuthState();
          return of(undefined);
        }),
      ),
    );
  }

  /**
   * Clear authentication state
   */
  private clearAuthState(): void {
    this.authState.set({ user: null });
  }

  /**
   * Request verification code (Step 1 of authentication)
   */
  signIn(email: string): Observable<void> {
    const credentials = btoa(`${email}:`);
    const headers = new HttpHeaders({
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .post<ApiResponse<void>>(`${this.baseUrl}/user/sign-in`, null, { headers })
      .pipe(map((response) => response.result));
  }

  /**
   * Exchange verification code for JWT cookies, then load the user from /user/me.
   * The access token is httpOnly, so the portal cannot read document.cookie.
   */
  exchangeCode(email: string, code: string): Observable<void> {
    const credentials = btoa(`${email}:${code}`);
    const headers = new HttpHeaders({
      Authorization: `Basic ${credentials}`,
    });

    return this.http
      .post<ApiResponse<{ message: string }>>(`${this.baseUrl}/user/exchange-code`, null, {
        headers,
      })
      .pipe(
        switchMap(() => this.http.get<ApiResponse<User>>(`${this.baseUrl}/user/me`)),
        tap((response) => this.authState.set({ user: response.result })),
        map(() => undefined),
      );
  }

  /**
   * Update current user data
   */
  updateUser(user: User): void {
    this.authState.set({ user });
  }

  /**
   * Sai da sessão e SEMPRE recarrega no login.
   *
   * `/user/logout` também exige sessão válida — então, justo quando o logout é disparado por
   * uma sessão que caiu (401), a chamada falha. Sair só no sucesso deixava a pessoa presa na
   * tela, cheia de erros e sem caminho de volta. O pedido à API é cortesia (apaga os cookies
   * do lado de lá); limpar o estado e ir para o login não depende dele.
   */
  logout(motivo?: MotivoSaida): Observable<void> {
    // Uma tela carrega várias coisas ao mesmo tempo; quando a sessão cai, todas voltam 401
    // juntas. Uma saída basta — as outras chamadas encontram a primeira em andamento.
    if (this.saindo) return of(undefined);
    this.saindo = true;
    return this.http
      .post<ApiResponse<{ message: string }>>(`${this.baseUrl}/user/logout`, null)
      .pipe(
        map(() => undefined),
        catchError(() => of(undefined)),
        finalize(() => {
          this.clearAuthState();
          // Navegação completa descarta componentes e mensagens de erro da tela anterior.
          // O Router apenas trocava a rota e deixava esses avisos visíveis no login.
          this.document.location.replace(this.destinoDoLogin(motivo));
        }),
      );
  }

  private saindo = false;

  /**
   * Saída pedida pela pessoa vai direto ao login. Saída forçada leva o motivo (o login avisa
   * por quê) e a tela de onde ela caiu, para voltar ao mesmo lugar depois de entrar.
   */
  private destinoDoLogin(motivo?: MotivoSaida): string {
    if (!motivo) return '/login';
    const params = new URLSearchParams({ sessao: motivo });
    const { pathname = '', search = '' } = this.document.location ?? {};
    if (pathname && !/^\/(login|verify-code)/.test(pathname)) {
      params.set('returnUrl', pathname + search);
    }
    return `/login?${params}`;
  }

  /**
   * Papéis que enxergam tudo, espelhando o bypass que o backend já faz no RoleGuard.
   */
  private static readonly SUPER_ROLES: readonly UserRole[] = [UserRole.ROOT, UserRole.ADMIN];

  /**
   * Autorização por lista explícita de papéis.
   *
   * AGENT e SERVICE_MANAGER são papéis IRMÃOS, não níveis de uma escada: o corretor não é
   * "mais" que o gestor predial, ele faz outro trabalho. Uma hierarquia numérica não consegue
   * representar isso — era ela que fazia o comercial inteiro vazar para o gestor predial.
   */
  /** Administrador (ou root): vê e faz tudo. */
  isAdmin(): boolean {
    const role = this.userRole();
    return !!role && AuthService.SUPER_ROLES.includes(role);
  }

  /**
   * A aba está liberada para quem está logado? Administrador: sempre. Membro: se está entre as
   * abas que o administrador marcou. Qualquer outro (cargo antigo, sem sessão): não.
   */
  hasTab(tab: string): boolean {
    if (this.isAdmin()) return true;
    const user = this.user();
    if (user?.role === UserRole.MEMBER) return (user.tabs ?? []).includes(tab);
    // Cargo antigo vindo de uma API ainda não atualizada: vale o que o cargo via no menu.
    const legado = LEGACY_ROLE_TABS[user?.role as keyof typeof LEGACY_ROLE_TABS];
    return !!legado && (legado as readonly string[]).includes(tab);
  }

  /** Basta uma das abas. */
  hasAnyTab(tabs: readonly string[]): boolean {
    return tabs.some((tab) => this.hasTab(tab));
  }

  hasAnyRole(allowedRoles: readonly UserRole[]): boolean {
    const currentRole = this.userRole();
    if (!currentRole) {
      return false;
    }

    if (AuthService.SUPER_ROLES.includes(currentRole)) {
      return true;
    }

    return allowedRoles.includes(currentRole);
  }

  /**
   * Verificação hierárquica, mantida APENAS para os papéis que são de fato uma escada
   * (ROOT > ADMIN). Para separar corretor de gestor predial, use `hasAnyRole`.
   */
  hasRole(requiredRole: UserRole): boolean {
    const currentRole = this.userRole();
    if (!currentRole) {
      return false;
    }

    const roleHierarchy: Record<UserRole, number> = {
      [UserRole.ROOT]: 4,
      [UserRole.ADMIN]: 3,
      [UserRole.MEMBER]: 1,
      [UserRole.AGENT]: 2,
      [UserRole.SERVICE_MANAGER]: 2,
      [UserRole.EDITOR]: 1,
    };

    return roleHierarchy[currentRole] >= roleHierarchy[requiredRole];
  }
}
